import QRCode from 'qrcode';
import { formatDateLong } from './dateUtils';

/**
 * The shift check-in QR poster — what a care-home manager prints from
 * Organisation settings and puts up on the wall. Carers scan it in the app
 * to start and to end a shift (api/utils/shiftQr.js).
 */

/** The code as SVG markup — sharp at any size, on screen or on paper. */
export const shiftQrSvg = (payload) =>
  QRCode.toString(payload, {
    type: 'svg',
    errorCorrectionLevel: 'M',
    margin: 0,
    color: { dark: '#000000', light: '#FFFFFF' },
  });

const esc = (value) => String(value ?? '').replace(/[&<>"']/g, (c) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[c]));

const posterHtml = ({ svg, title, siteName, logoUrl, reference, generatedAt }) => `<!doctype html>
<html><head><meta charset="utf-8"><title>Shift check-in QR</title>
<style>
  @page { size: A4 portrait; margin: 14mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; background: #fff; color: #0B1220;
    font-family: -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; }
  .page { min-height: 268mm; display: flex; flex-direction: column; align-items: center; text-align: center; }
  .logo { height: 22mm; max-width: 60mm; object-fit: contain; margin-bottom: 5mm; }
  h1 { font-size: 26pt; margin: 0; line-height: 1.15; }
  .site { font-size: 15pt; color: #475569; margin-top: 2mm; }
  .ask { font-size: 30pt; font-weight: 800; margin: 10mm 0 7mm; color: #1D4ED8; }
  .code { width: 125mm; height: 125mm; padding: 6mm; border: 1.2mm solid #0B1220; border-radius: 6mm; }
  .code svg { width: 100%; height: 100%; display: block; }
  ol { text-align: left; font-size: 14pt; line-height: 1.7; margin: 9mm 0 0; padding-left: 7mm; }
  .foot { margin-top: auto; font-size: 9.5pt; color: #64748B; line-height: 1.5; }
</style></head>
<body><div class="page">
  ${logoUrl ? `<img class="logo" src="${esc(logoUrl)}" alt="">` : ''}
  <h1>${esc(title)}</h1>
  ${siteName ? `<div class="site">${esc(siteName)}</div>` : ''}
  <div class="ask">Scan to start and end your shift</div>
  <div class="code">${svg}</div>
  <ol>
    <li>Open the ServPatch app.</li>
    <li>Tap <strong>Start shift</strong> or <strong>End shift</strong> — the camera opens.</li>
    <li>Point it at this code. Your shift starts or ends once it is scanned.</li>
  </ol>
  <div class="foot">
    Code ref <strong>${esc(reference)}</strong> · made ${esc(formatDateLong(generatedAt))}<br>
    If a new code is made in Organisation settings, this one stops working — replace this poster.
  </div>
</div></body></html>`;

/**
 * Open the browser's print dialog on the poster. Printed from a hidden frame,
 * so it cannot be stopped by a popup blocker and the page behind is untouched;
 * "Save as PDF" in that dialog covers a manager who wants a file instead.
 *
 * @param {object} qr  the GET /organization/shift-qr data, with `logo_url`
 *                     already turned into an absolute URL
 */
export async function printShiftQrPoster(qr) {
  const svg = await shiftQrSvg(qr.payload);

  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
  document.body.appendChild(frame);

  const doc = frame.contentDocument;
  doc.open();
  doc.write(posterHtml({
    svg,
    title: qr.title,
    siteName: qr.site_name,
    logoUrl: qr.logo_url,
    reference: qr.reference,
    generatedAt: qr.generated_at,
  }));
  doc.close();

  // The logo is the only thing that loads; printing before it arrives prints a
  // gap. A broken logo must not stop the poster, so errors count as loaded.
  await Promise.all([...doc.images].map((img) => (img.complete ? null : new Promise((done) => {
    img.onload = done;
    img.onerror = done;
  }))));

  const win = frame.contentWindow;
  // Some browsers return from print() before the dialog closes; removing the
  // frame then would cancel the job, so wait for afterprint (or a minute).
  const remove = () => frame.remove();
  win.addEventListener('afterprint', remove, { once: true });
  setTimeout(remove, 60000);
  win.focus();
  win.print();
}
